import { fmtCVE0 } from "@/lib/format";
import { periodLabel, bestSaving, planPrice, waLink } from "@/lib/pricing";
import { Check, Crown, WhatsappLogo, EnvelopeSimple, Buildings } from "@phosphor-icons/react";

const fmt = (v) => (v === null || v === undefined ? "[a definir]" : fmtCVE0(v));

export function PeriodSelector({ plans, durations, months, setMonths, dark = false }) {
  return (
    <div className={`inline-flex flex-wrap rounded-full p-1 ${dark ? "bg-slate-100" : "bg-muted"}`} data-testid="period-selector">
      {durations.map((d) => {
        const off = bestSaving(plans, d);
        const on = months === d;
        return (
          <button key={d} type="button" onClick={() => setMonths(d)} data-testid={`period-${d}`}
            className={`flex items-center gap-1.5 rounded-full px-2.5 py-2 text-xs font-semibold sm:px-4 sm:text-sm transition-colors ${on ? (dark ? "bg-petrol text-white" : "bg-card shadow-sm") : (dark ? "text-slate-600" : "text-muted-foreground")}`}>
            {periodLabel(d)}
            {off > 0 && <span className="rounded-full bg-mint px-1.5 py-0.5 text-[10px] font-extrabold text-mint-ink" data-testid={`period-saving-${d}`}>−{off}%</span>}
          </button>
        );
      })}
    </div>
  );
}

export function PlanPrice({ plan, months, muted = "text-muted-foreground", strike = true }) {
  const { base, final, isFounder } = planPrice(plan, months);
  const perMonth = (v) => (v === null || v === undefined ? v : Math.round(v / months));
  return (
    <div data-testid={`plan-price-${plan.key}`}>
      {isFounder && strike && <div className={`text-sm line-through ${muted}`} data-testid={`plan-price-old-${plan.key}`}>{fmt(perMonth(base))}/mês</div>}
      <div className="flex items-end gap-1">
        <span className="font-display text-4xl font-extrabold tracking-tight" data-testid={`plan-price-final-${plan.key}`}>{fmt(perMonth(final))}</span>
        <span className={`mb-1 text-sm ${muted}`}>/mês</span>
      </div>
      <div className={`text-xs ${muted}`} data-testid={`plan-price-total-${plan.key}`}>
        {months === 1 ? "Pago todos os meses" : `${fmt(final)} pagos a cada ${months} meses`}
      </div>
    </div>
  );
}

export function FounderBanner({ founder, dark = false }) {
  if (!founder) return null;
  if (founder.state === "active") {
    return (
      <div className={`flex items-center gap-3 rounded-2xl border p-4 text-sm ${dark ? "border-warn/60 bg-warn/15 text-ink" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="founder-banner">
        <Crown size={22} weight="fill" className="shrink-0 text-warn" />
        <span>Empresa Fundadora nº {founder.number} · o seu desconto de −{founder.pct}% mantém-se enquanto a subscrição estiver ativa.</span>
      </div>
    );
  }
  if (!founder.eligible) return null;
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-2xl border p-4 text-sm ${dark ? "border-warn/60 bg-warn/15 text-ink" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="founder-banner">
      <Crown size={22} weight="fill" className="shrink-0 text-warn" />
      <span className="flex-1">
        <b>Preço Fundador −{founder.pct}%</b> para as primeiras {founder.max_orgs} empresas que ativam um plano pago — para sempre, enquanto a subscrição estiver ativa.
      </span>
      <span className="rounded-full bg-petrol px-3 py-1 text-xs font-bold text-white" data-testid="founder-seats-left">Restam {founder.seats_left} lugares</span>
    </div>
  );
}

export function PlanFeatures({ features, iconClass = "text-primary" }) {
  return (
    <ul className="mt-5 flex-1 space-y-2 text-sm">
      {(features || []).map((f) => <li key={f} className="flex gap-2"><Check size={16} weight="bold" className={`mt-0.5 shrink-0 ${iconClass}`} />{f}</li>)}
    </ul>
  );
}

export function EnterpriseCard({ plan, contact, className = "" }) {
  const c = contact || {};
  return (
    <div className={`flex flex-col rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-7 text-ink ${className}`} data-testid="plan-card-empresa">
      <div className="flex items-center gap-2"><Buildings size={22} weight="bold" className="text-petrol" /><h3 className="font-display text-xl font-extrabold">{plan?.name || "Empresa"}</h3></div>
      <div className="mt-4 font-display text-2xl font-extrabold">Sob consulta</div>
      <p className="mt-1 text-sm text-slate-600" data-testid="empresa-text">{c.text}</p>
      <PlanFeatures features={plan?.features} iconClass="text-petrol" />
      <div className="mt-6 grid gap-2">
        {c.whatsapp ? (
          <a href={waLink(c.whatsapp)} target="_blank" rel="noreferrer" data-testid="empresa-whatsapp"
            className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] py-3 font-bold text-white transition-transform hover:-translate-y-0.5"><WhatsappLogo size={18} weight="fill" /> WhatsApp</a>
        ) : <span className="rounded-full border border-slate-200 py-3 text-center text-sm text-slate-500" data-testid="empresa-whatsapp-missing">WhatsApp [a definir]</span>}
        {c.email ? (
          <a href={`mailto:${c.email}?subject=${encodeURIComponent("Plano Empresa GestaCV")}`} data-testid="empresa-email"
            className="flex items-center justify-center gap-2 rounded-full border border-petrol py-3 font-bold transition-colors hover:bg-petrol hover:text-white"><EnvelopeSimple size={18} weight="bold" /> Enviar email</a>
        ) : <span className="rounded-full border border-slate-200 py-3 text-center text-sm text-slate-500" data-testid="empresa-email-missing">Email [a definir]</span>}
      </div>
    </div>
  );
}


export function ContactLine({ contact, className = "" }) {
  const c = contact || {};
  return (
    <p className={`rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-4 text-center text-sm text-slate-700 ${className}`} data-testid="plans-contact-line">
      Mais de 3 utilizadores? <b>Fale connosco</b>
      {c.whatsapp && <> · <a href={waLink(c.whatsapp)} target="_blank" rel="noreferrer" className="font-semibold text-petrol underline" data-testid="plans-contact-whatsapp">WhatsApp</a></>}
      {c.email && <> · <a href={`mailto:${c.email}`} className="font-semibold text-petrol underline" data-testid="plans-contact-email">{c.email}</a></>}
    </p>
  );
}
